import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MapsHomeWorkRoundedIcon from '@mui/icons-material/MapsHomeWorkRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import moment from 'moment';
import { HALL_EVENT_PAYMENT_METHOD_OPTIONS } from '../utils/hallEventForm';
import { formatPhoneWithDialCode } from '../../../utils/phoneValidation';

const HALL_ACCENT = '#4ce1b6';
const WARNING = '#f6c453';
const SECONDARY = '#5b9cf5';
const DANGER = '#f07178';

const EVENT_TYPE_LABELS = {
  wedding: 'Wedding',
  engagement: 'Engagement',
  reception: 'Reception',
  sangeet: 'Sangeet / Haldi',
  birthday: 'Birthday / Party',
  conference: 'Conference',
  seminar: 'Seminar / Training',
  exhibition: 'Exhibition',
  corporate: 'Corporate Event',
  social: 'Social Gathering',
  other: 'Other',
};

function statusMeta(status) {
  const key = String(status || '').toLowerCase();
  const map = {
    confirmed: { color: HALL_ACCENT, label: 'Confirmed' },
    tentative: { color: WARNING, label: 'Tentative' },
    completed: { color: SECONDARY, label: 'Completed' },
    cancelled: { color: DANGER, label: 'Cancelled' },
  };
  return map[key] || { color: SECONDARY, label: key ? key.replace(/_/g, ' ') : '—' };
}

function paymentTypeLabel(value) {
  const key = String(value || '').toLowerCase();
  if (key === 'partial') return 'Partial Payment';
  if (key === 'due') return 'Due';
  return 'Full Payment';
}

function paymentModeLabel(value) {
  const match = HALL_EVENT_PAYMENT_METHOD_OPTIONS.find(
    (option) => option.value === String(value || '').toLowerCase(),
  );
  return match?.label || String(value || '—').replace(/_/g, ' ').toUpperCase();
}

function formatDateLabel(date) {
  const parsed = moment(date, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : '—';
}

function formatTimeLabel(time, prefix = '') {
  if (!time) return '';
  const parsed = moment(time, 'HH:mm');
  const label = parsed.isValid() ? parsed.format('HH:mm') : String(time);
  return prefix ? `${prefix} ${label}` : label;
}

function ScheduleChip({
  label,
  date,
  time,
  tone = 'start',
  isDark,
}) {
  const isEnd = tone === 'end';
  const accent = isEnd ? '#f97316' : HALL_ACCENT;
  const softBg = isDark
    ? alpha(accent, isEnd ? 0.18 : 0.16)
    : (isEnd ? alpha('#ffedd5', 0.95) : alpha('#d9f5ea', 0.95));
  const labelColor = isDark ? accent : (isEnd ? '#c2410c' : '#0f766e');
  const valueColor = isDark ? '#f8fafc' : (isEnd ? '#7c2d12' : '#0f3d32');
  const subtleColor = isDark ? alpha('#fff', 0.62) : (isEnd ? '#9a3412' : '#54756a');

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 1.4,
        py: 1.15,
        borderRadius: '18px',
        border: 'none',
        bgcolor: softBg,
        boxShadow: isDark ? 'none' : `0 8px 18px ${alpha(accent, 0.14)}`,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.45 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: accent,
            boxShadow: `0 0 0 3px ${alpha(accent, isDark ? 0.22 : 0.18)}`,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            color: labelColor,
            fontSize: '0.68rem',
            fontWeight: 900,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        sx={{
          color: valueColor,
          fontWeight: 900,
          fontSize: '0.98rem',
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
        }}
      >
        {date}
      </Typography>
      {time ? (
        <Typography sx={{ mt: 0.25, color: subtleColor, fontWeight: 700, fontSize: '0.78rem' }}>
          {time}
        </Typography>
      ) : null}
    </Box>
  );
}

function InfoRow({ icon, label, value, isDark, tokens }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: isDark ? alpha(HALL_ACCENT, 0.14) : alpha(HALL_ACCENT, 0.12),
          color: HALL_ACCENT,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
        <Typography
          sx={{
            color: tokens.subtleText,
            fontSize: '0.68rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            color: tokens.text,
            fontWeight: 800,
            fontSize: '0.92rem',
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {value || '—'}
        </Typography>
      </Box>
    </Stack>
  );
}

function SectionCard({ title, children, isDark, tokens }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '18px',
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
        bgcolor: isDark ? alpha('#fff', 0.035) : alpha('#fff', 0.72),
        boxShadow: isDark ? 'none' : `0 10px 24px ${alpha('#0f172a', 0.05)}`,
      }}
    >
      <Typography
        sx={{
          mb: 1.25,
          color: HALL_ACCENT,
          fontWeight: 900,
          fontSize: '0.7rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
      <Stack spacing={1.35}>{children}</Stack>
    </Box>
  );
}

export default function HallBookingViewSummary({
  form,
  hallName,
  eventId,
  isDark,
  tokens,
  formatCurrency,
}) {
  const status = statusMeta(form.status);
  const phoneDisplay = formatPhoneWithDialCode(
    form.customer_phone_country_code || '+91',
    form.customer_phone,
  );
  const scheduleLabel = form.booking_type === 'day'
    ? 'Full Day · 24 hours'
    : `Hourly · ${Number(form.duration_hours || 0) || '—'}h`;
  const dateRange = form.end_date && form.end_date !== form.event_date
    ? `${moment(form.event_date).format('DD MMM YYYY')} → ${moment(form.end_date).format('DD MMM YYYY')}`
    : moment(form.event_date).format('DD MMM YYYY');

  return (
    <Stack spacing={1.75}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: 2,
          borderRadius: '20px',
          border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.28) : alpha(HALL_ACCENT, 0.22)}`,
          background: isDark
            ? `linear-gradient(145deg, ${alpha('#102a24', 0.95)} 0%, ${alpha('#0d1c2e', 0.96)} 100%)`
            : `linear-gradient(145deg, #f3fffa 0%, #f5f9ff 100%)`,
          boxShadow: isDark
            ? `0 16px 36px ${alpha('#000', 0.28)}`
            : `0 14px 30px ${alpha(HALL_ACCENT, 0.12)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: tokens.subtleText, fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.06em' }}>
              {eventId || 'EVENT'}
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                color: tokens.text,
                fontWeight: 950,
                fontSize: '1.2rem',
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
              }}
            >
              {form.event_name || 'Untitled event'}
            </Typography>
            <Typography sx={{ mt: 0.55, color: tokens.subtleText, fontWeight: 700, fontSize: '0.82rem' }}>
              {hallName || '—'} · {dateRange}
            </Typography>
          </Box>
          <Chip
            label={status.label}
            size="small"
            sx={{
              height: 28,
              borderRadius: '999px',
              fontWeight: 900,
              textTransform: 'capitalize',
              color: isDark ? '#04120f' : '#0f172a',
              bgcolor: alpha(status.color, isDark ? 0.92 : 0.85),
              border: `1px solid ${alpha(status.color, 0.65)}`,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Chip
            size="small"
            label={scheduleLabel}
            sx={{
              fontWeight: 800,
              borderRadius: '10px',
              bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.05),
              color: tokens.text,
            }}
          />
          <Chip
            size="small"
            label={EVENT_TYPE_LABELS[form.event_type] || form.event_type || 'Event'}
            sx={{
              fontWeight: 800,
              borderRadius: '10px',
              bgcolor: isDark ? alpha(SECONDARY, 0.18) : alpha(SECONDARY, 0.12),
              color: isDark ? '#dbeafe' : '#1e3a5f',
            }}
          />
          <Chip
            size="small"
            label={formatCurrency?.(form.amount) || form.amount || '—'}
            sx={{
              fontWeight: 900,
              borderRadius: '10px',
              bgcolor: alpha(HALL_ACCENT, isDark ? 0.2 : 0.16),
              color: isDark ? HALL_ACCENT : '#0f766e',
            }}
          />
        </Stack>
      </Box>

      <SectionCard title="Schedule" isDark={isDark} tokens={tokens}>
        <Stack direction="row" spacing={1.5} useFlexGap>
          <ScheduleChip
            label="Start"
            date={formatDateLabel(form.event_date)}
            time={formatTimeLabel(form.start_time, 'From')}
            tone="start"
            isDark={isDark}
          />
          <ScheduleChip
            label="End"
            date={formatDateLabel(form.end_date || form.event_date)}
            time={formatTimeLabel(form.end_time, 'Until')}
            tone="end"
            isDark={isDark}
          />
        </Stack>

        <Chip
          icon={<MapsHomeWorkRoundedIcon sx={{ fontSize: '18px !important' }} />}
          label={`${hallName || 'Hall'} · ${form.guests || 0} Guests`}
          sx={{
            alignSelf: 'flex-start',
            height: 'auto',
            py: 1,
            px: 0.5,
            borderRadius: '999px',
            border: 'none',
            fontWeight: 800,
            color: isDark ? HALL_ACCENT : '#0f3d32',
            bgcolor: isDark ? alpha(HALL_ACCENT, 0.16) : alpha('#d9f5ea', 0.95),
            boxShadow: isDark ? 'none' : `0 8px 18px ${alpha(HALL_ACCENT, 0.14)}`,
            '& .MuiChip-icon': {
              color: isDark ? HALL_ACCENT : '#0f766e',
              ml: 0.75,
            },
            '& .MuiChip-label': {
              py: 0.35,
              px: 1,
              whiteSpace: 'normal',
              lineHeight: 1.3,
            },
          }}
        />
      </SectionCard>

      <SectionCard title="Host & Guests" isDark={isDark} tokens={tokens}>
        <InfoRow
          icon={<PersonRoundedIcon sx={{ fontSize: 18 }} />}
          label="Host / Customer"
          value={form.customer_name}
          isDark={isDark}
          tokens={tokens}
        />
        <InfoRow
          icon={<PhoneRoundedIcon sx={{ fontSize: 18 }} />}
          label="Phone"
          value={phoneDisplay}
          isDark={isDark}
          tokens={tokens}
        />
        <InfoRow
          icon={<EmailRoundedIcon sx={{ fontSize: 18 }} />}
          label="Email"
          value={form.customer_email || '—'}
          isDark={isDark}
          tokens={tokens}
        />
        <Divider sx={{ borderColor: alpha(tokens.text, 0.08) }} />
        <InfoRow
          icon={<GroupsRoundedIcon sx={{ fontSize: 18 }} />}
          label="Guests"
          value={`${form.guests || 0} total · ${form.adults || 0} adults · ${form.children || 0} children`}
          isDark={isDark}
          tokens={tokens}
        />
      </SectionCard>

      <SectionCard title="Payment" isDark={isDark} tokens={tokens}>
        <InfoRow
          icon={<PaymentsRoundedIcon sx={{ fontSize: 18 }} />}
          label="Total Amount"
          value={formatCurrency?.(form.amount) || form.amount || '—'}
          isDark={isDark}
          tokens={tokens}
        />
        <InfoRow
          icon={<EventRoundedIcon sx={{ fontSize: 18 }} />}
          label="Payment Type"
          value={paymentTypeLabel(form.payment_type)}
          isDark={isDark}
          tokens={tokens}
        />
        {form.payment_type !== 'due' ? (
          <InfoRow
            icon={<PaymentsRoundedIcon sx={{ fontSize: 18 }} />}
            label={form.payment_type === 'partial' ? 'Advance Amount' : 'Payment Amount'}
            value={formatCurrency?.(
              form.payment_type === 'full' ? form.amount : form.advance_amount,
            ) || form.advance_amount || '—'}
            isDark={isDark}
            tokens={tokens}
          />
        ) : null}
        {form.payment_type !== 'due' ? (
          <InfoRow
            icon={<PaymentsRoundedIcon sx={{ fontSize: 18 }} />}
            label="Payment Method"
            value={paymentModeLabel(form.payment_mode)}
            isDark={isDark}
            tokens={tokens}
          />
        ) : null}
      </SectionCard>

      {form.notes ? (
        <SectionCard title="Notes" isDark={isDark} tokens={tokens}>
          <InfoRow
            icon={<NotesRoundedIcon sx={{ fontSize: 18 }} />}
            label="Special Requirements"
            value={form.notes}
            isDark={isDark}
            tokens={tokens}
          />
        </SectionCard>
      ) : null}
    </Stack>
  );
}
